import React, { useEffect, useState, useCallback } from "react";
import { Loader, Container } from "semantic-ui-react";
import { DidacticMaterialsAPI, VirtualScenes } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { SalasItemSalas } from "../SalasItem_Salas";
import { RutasDidacticasForm } from "../../RutasDidacticasComp";
import "./SalasList.scss";

const didacticMaterialsController = new DidacticMaterialsAPI();
const virtualScenesController = new VirtualScenes();

// Mapa de clases CSS por categoría
const CATEGORY_MAP = {
  "CIENCIAS HUMANAS": "e-button-humanas",
  "CIENCIAS NATURALES": "e-button-naturales",
  HISTORIA: "e-button-historia",
  MATEMÁTICAS: "e-button-matematicas",
  ARTE: "e-button-arte",
  "VIDA SALUDABLE": "e-button-vida-saludable",
  OTROS: "e-button-otros",
};

// Nuevo label para el botón "OTROS"
const OTHER_LABEL = "MIS ESCENAS";

export function SalasList({
  classNumber,
  teacher,
  currentPage,
  onPageChange,
  onTotalPagesChange,
  reload,
  onReload,
}) {
  const [categories, setCategories] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(
    localStorage.getItem("selectedCategory") || null
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    localStorage.getItem("selectedSubcategory") || null
  );
  const [showRutasForm, setShowRutasForm] = useState(false);
  const [selectedScene, setSelectedScene] = useState(null);

  const { accessToken, user } = useAuth();

  const getCategoryClass = (name) => CATEGORY_MAP[name] || "";

  const saveFiltersToLocalStorage = (category, subcategory) => {
    if (category !== undefined) {
      setSelectedCategory(category);
      localStorage.setItem("selectedCategory", category || "");
    }
    if (subcategory !== undefined) {
      setSelectedSubcategory(subcategory);
      localStorage.setItem("selectedSubcategory", subcategory || "");
    }
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setDataLoaded(false);
    try {
      const response = await didacticMaterialsController.getMaterialsByRegion(
        accessToken,
        "Europe"
      );
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error("Error al cargar categorías:", error);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const fetchScenes = useCallback(
    async (page = 1) => {
      setLoading(true);
      setDataLoaded(false);
      try {
        const response = await virtualScenesController.getVirtualScenesMini(
          { page, limit: 300 },
          accessToken
        );
        let allScenes = response.miniatureData || [];

        if (selectedCategory === "OTROS") {
          // Lógica "MIS ESCENAS"
          allScenes = allScenes.filter(
            (s) => s.account === "creator" && s.userId === user.user._id
          );
        } else {
          // Resto de categorías
          allScenes = allScenes.filter((s) => s.account !== "creator");
          if (selectedCategory) {
            allScenes = allScenes.filter((s) =>
              s.category.includes(selectedCategory)
            );
          }
          if (selectedSubcategory) {
            allScenes = allScenes.filter((s) =>
              s.subcategory.includes(selectedSubcategory)
            );
          }
        }

        setScenes(allScenes);
        onTotalPagesChange(response.totalPages || 1);

        // Precarga de imágenes
        await Promise.all(
          allScenes.map(
            (scene) =>
              new Promise((resolve) => {
                const img = new Image();
                img.src = scene.bannertopUrl;
                img.onload = resolve;
                img.onerror = resolve;
              })
          )
        );
        setDataLoaded(true);
      } catch (error) {
        console.error("Error al cargar escenas:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      accessToken,
      selectedCategory,
      selectedSubcategory,
      onTotalPagesChange,
      user.user._id,
    ]
  );

  useEffect(() => {
    fetchCategories();
    fetchScenes(currentPage);
  }, [fetchCategories, fetchScenes, currentPage]);

  const handleAllScenes = () => {
    saveFiltersToLocalStorage(null, null);
    fetchScenes(1);
    onPageChange(1);
  };
  const handleSelect = (category = null, subcategory = null) => {
    saveFiltersToLocalStorage(category, subcategory);
    fetchScenes(1);
    onPageChange(1);
  };
  const handleUploadScene = (scene) => {
    setSelectedScene(scene);
    setShowRutasForm(true);
  };
  const handleCloseRutasForm = () => {
    setShowRutasForm(false);
    setSelectedScene(null);
  };

  return (
    <div className="salas-list">
      {loading || !dataLoaded ? (
        <Loader active inline="centered">
          Cargando datos y escenas...
        </Loader>
      ) : (
        <Container className="ui-materials">
          {/* --- Introducción --- */}
          <div className="intro-sections">
            <div className="intro-section rutas-creadas">
              <h2 className="intro-title">
                ¡Hola! Estás en el Catálogo de Escenas VR de EDUCA360
              </h2>
              <p className="intro-description">
                Filtra por categoría y escoge la escena con la que quieras
                trabajar hoy. Aquí también puedes crear tus propios itinerarios
                didácticos.
              </p>
              <hr className="intro-divider" />
            </div>
          </div>

          {/* --- Categorías --- */}
          <div className="salas-list__buttons">
            <button
              onClick={handleAllScenes}
              className={`e-button-categories e-button-todas ${
                !selectedCategory ? "active" : ""
              }`}
            >
              TODAS
            </button>
            {categories.map((cat) => (
              <button
                key={cat._id}
                onClick={() => handleSelect(cat.name, null)}
                className={`
        e-button-categories ${getCategoryClass(cat.name)}
        ${selectedCategory === cat.name ? "active" : ""}
      `}
              >
                {cat.name}
              </button>
            ))}
            <button
              onClick={() => handleSelect("OTROS", null)}
              className={`
      e-button-categories ${getCategoryClass("OTROS")}
      ${selectedCategory === "OTROS" ? "active" : ""}
    `}
            >
              {OTHER_LABEL}
            </button>
          </div>

          {/* --- Subcategorías (alineadas) --- */}
          {selectedCategory && selectedCategory !== "OTROS" && (
            <>
              <div className="enunciado-sections">
                <p className="intro-categories">{selectedCategory}</p>
              </div>
              <div className="salas-list__subcategories">
                {categories
                  .find((cat) => cat.name === selectedCategory)
                  ?.subcategories.map((sub) => (
                    <button
                      key={sub._id}
                      onClick={() => handleSelect(selectedCategory, sub.name)}
                      className={`e-button-subcategories ${
                        selectedSubcategory === sub.name ? "active" : ""
                      }`}
                    >
                      {sub.name.toUpperCase()}
                    </button>
                  ))}
              </div>
            </>
          )}

          {/* --- Escenas --- */}
          <div className={`salas-list__scenes${scenes.length === 0 ? " empty" : ""}`}>
            {scenes.length > 0 ? (
              scenes.map((scene) => (
                <div
                  className="salas-list__scenes__salascolumn"
                  key={scene.refId}
                >
                  <SalasItemSalas
                    scene={scene}
                    teacher={teacher}
                    onUploadScene={handleUploadScene}
                  />
                </div>
              ))
            ) : (
              <div  className="intro-sections">
                <div className="intro-section rutas-creadas">
                  <h2 className="intro-title">
                    Educa360 está continuamente ampliando el catálogo de escenas.
                  </h2>
                  <p className="intro-description">
                    Pronto tendrás novedades en este apartado.
              </p>
              <hr className="intro-divider" />
            </div>
          </div>
            )}
          </div>
        </Container>
      )}

      {showRutasForm && (
        <RutasDidacticasForm
          accessToken={accessToken}
          classNumber={classNumber}
          teacher={teacher}
          scene={selectedScene}
          onClose={handleCloseRutasForm}
          reload={reload}
          onReload={onReload}
        />
      )}
    </div>
  );
}
